import React, { useState } from 'react';
import {
    Box, Card, CardContent, Tab, Tabs, TextField,
    Button, Typography, Alert, CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import './Login.css';

function Login() {
    const [tab, setTab] = useState(0); // 0 = sign in, 1 = create account
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendingVerification, setResendingVerification] = useState(false);
    const [needsVerification, setNeedsVerification] = useState(false);

    const { login, register, resendVerificationEmail } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from || '/';

    const handleTabChange = (_, newValue) => {
        setTab(newValue);
        setError('');
        setSuccess('');
        setNeedsVerification(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setNeedsVerification(false);
        setLoading(true);
        try {
            if (tab === 0) {
                await login(email, password);
                navigate(redirectTo, { replace: true });
            } else {
                if (password.length < 8) {
                    setError('Password must be at least 8 characters.');
                    setLoading(false);
                    return;
                }
                if (!firstName.trim() || !lastName.trim()) {
                    setError('First name and last name are required.');
                    setLoading(false);
                    return;
                }
                const data = await register(email, password, firstName.trim(), lastName.trim());
                if (data.email_verification_sent) {
                    setSuccess(`Account created. We sent a verification email to ${email}. Verify your email before signing in.`);
                } else {
                    setSuccess('Account created, but we could not send the verification email. Please contact support before signing in.');
                }
                setPassword('');
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 403 && detail === 'Please verify your email before signing in.') {
                setNeedsVerification(true);
            }
            setError(detail || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        setError('');
        setSuccess('');
        setResendingVerification(true);
        try {
            await resendVerificationEmail(email, password);
            setSuccess(`Verification email resent to ${email}.`);
            setNeedsVerification(false);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(detail || 'Could not resend the verification email. Please try again.');
        } finally {
            setResendingVerification(false);
        }
    };

    return (
        <Box className="login-page">
            <Helmet>
                <title>{tab === 0 ? 'Sign In' : 'Create Account'} | Hockey Pipeline</title>
            </Helmet>

            <Card className="login-card" elevation={0}>
                <CardContent sx={{ p: 3 }}>
                    <div className="login-logo">
                        <img src="/logo@4x.png" alt="Hockey Pipeline" />
                    </div>

                    <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        className="login-tabs"
                        sx={{ mb: 3 }}
                    >
                        <Tab label="Sign In" />
                        <Tab label="Create Account" />
                    </Tabs>

                    {/* Email / password form */}
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        {tab === 1 && (
                            <>
                                <TextField
                                    fullWidth
                                    required
                                    label="First name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    margin="normal"
                                    autoComplete="given-name"
                                    sx={fieldSx}
                                />
                                <TextField
                                    fullWidth
                                    required
                                    label="Last name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    margin="normal"
                                    autoComplete="family-name"
                                    sx={fieldSx}
                                />
                            </>
                        )}
                        <TextField
                            fullWidth
                            required
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            margin="normal"
                            autoComplete="email"
                            sx={fieldSx}
                        />
                        <TextField
                            fullWidth
                            required
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            margin="normal"
                            autoComplete={tab === 0 ? 'current-password' : 'new-password'}
                            helperText={tab === 1 ? 'Minimum 8 characters' : ''}
                            sx={fieldSx}
                        />

                        {error && (
                            <Alert severity="error" sx={{ mt: 2, borderRadius: 2, backgroundColor: '#2d1515', color: '#fca5a5', '& .MuiAlert-icon': { color: '#f87171' } }}>
                                {error}
                                {needsVerification && (
                                    <Button
                                        type="button"
                                        variant="text"
                                        size="small"
                                        disabled={resendingVerification}
                                        onClick={handleResendVerification}
                                        sx={{ ml: 1, color: '#fca5a5', textDecoration: 'underline', fontWeight: 700 }}
                                    >
                                        {resendingVerification ? 'Sending...' : 'Resend verification email'}
                                    </Button>
                                )}
                            </Alert>
                        )}

                        {success && (
                            <Alert severity="success" sx={{ mt: 2, borderRadius: 2, backgroundColor: '#102417', color: '#bbf7d0', '& .MuiAlert-icon': { color: '#86efac' } }}>
                                {success}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            className="login-submit-btn"
                            disableElevation
                        >
                            {loading
                                ? <CircularProgress size={22} sx={{ color: '#fff' }} />
                                : tab === 0 ? 'Sign In' : 'Create Account'
                            }
                        </Button>
                    </Box>

                    {tab === 0 && (
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mt: 2, color: '#6b7280', cursor: 'pointer', '&:hover': { color: '#e2e8f0' } }}
                            onClick={() => setTab(1)}
                        >
                            Don't have an account? <span className="login-link-accent">Sign up</span>
                        </Typography>
                    )}
                    {tab === 1 && (
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mt: 2, color: '#6b7280', cursor: 'pointer', '&:hover': { color: '#e2e8f0' } }}
                            onClick={() => setTab(0)}
                        >
                            Already have an account? <span className="login-link-accent">Sign in</span>
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        color: 'var(--text-primary)',
        borderRadius: 2,
        '& fieldset': { borderColor: '#2d3344' },
        '&:hover fieldset': { borderColor: '#4a5568' },
        '&.Mui-focused fieldset': { borderColor: 'var(--brand-strong)' },
    },
    '& .MuiInputLabel-root': { color: 'var(--text-muted)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--brand-strong)' },
    '& .MuiFormHelperText-root': { color: 'var(--text-muted)' },
};

export default Login;
